// The PVs of a space (the project's and the shared space's "Réunions" tab): number, title, date,
// status and how many actions. Drafts are listed by the API only to the people who can contribute.
import { DateText, StatusChip } from '@batiflow/ui';
import {
  Button,
  Link,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import { Document16Regular } from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useSpaceMinutes } from './api';

const useStyles = __css({"section":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"heading":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bg96gwp":"f1i3iumi","Bhrd7zp":"fl43uef"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"row":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","Belr9w4":"f778t4r","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f1o0qvyv","De3pzq":"fxugw4r","gggo0z":"fi15kbk","B834pou":"f1taw3pw","F1poyb":"foi3q75","Behc1zw":"f10zy6vx","uk9mye":"f1p2dij1","mbw9mh":"f1a566p","B4m11q7":"f32hy0g","rm1204":"fwyddsq","ijz38e":"f1ftuoky","tzxy30":"fgmlvap","Bwezv66":"fwl1lcr","Ctt3hk":"f1crbv9w"},"main":{"Bh6795r":"fqerorx","xawz":"f1name99","Bgto7am":"fuzgk9b","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62"},"title":{"Btd35i7":"fjksvth","Bhrd7zp":"fl43uef"},"meta":{"sj55zd":"fkfq4zb","Be2twd7":"fy9rknc"},"ltr":{"fz3jsb":"f1vfkb3d","kgocjf":["f1r2v9sp","f1va4sj6"]}});

export function MinutesSection({ spaceId }: { spaceId: string }) {
  const { t } = useTranslation('meetings');
  const styles = useStyles();
  const navigate = useNavigate();
  const query = useSpaceMinutes(spaceId);
  const items = query.data?.items ?? [];

  if (query.isError)
    return (
      <MessageBar intent="error" layout="multiline">
        <MessageBarBody>{t('minutes.list.loadFailed')}</MessageBarBody>
        <MessageBarActions>
          <Button
            onClick={() => {
              void query.refetch();
            }}
          >
            {t('list.retry')}
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  if (items.length === 0) return null;

  return (
    <section className={styles.section} aria-labelledby="minutes-list-heading">
      <h2 id="minutes-list-heading" className={styles.heading}>
        {t('minutes.list.title')}
      </h2>
      <ul className={styles.list}>
        {items.map((item) => {
          const href = `/minutes/${encodeURIComponent(spaceId)}/${encodeURIComponent(item.id)}`;
          return (
            <li key={item.id} className={styles.row}>
              <Document16Regular aria-hidden="true" />
              <span className={styles.main}>
                <Link
                  as="a"
                  href={href}
                  className={styles.title}
                  onClick={(event: React.MouseEvent) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)
                      return;
                    event.preventDefault();
                    void navigate({
                      to: '/minutes/$spaceId/$minutesId',
                      params: { spaceId, minutesId: item.id },
                    });
                  }}
                >
                  {item.number ? (
                    <>
                      <span className={styles.ltr}>{item.number}</span> · {item.title}
                    </>
                  ) : (
                    item.title
                  )}
                </Link>
                <span className={styles.meta}>
                  <DateText value={item.heldAt} variant="dateTime" />
                  {' · '}
                  {t('minutes.list.actions', { count: item.actionCount })}
                </span>
              </span>
              {item.status === 'published' ? (
                <StatusChip intent="success" label={t('minutes.status.published')} />
              ) : (
                <StatusChip intent="warning" label={t('minutes.status.draft')} />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}


import "/opt/batiflow/app/apps/web/src/features/minutes/minutes-list.griffel.css";